import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Wallet, Globe, Bell, Shield, HelpCircle, LogOut, ChevronRight, Copy, CreditCard as CreditCardIcon, Plus, Trash2, RefreshCw, Link2, UserX, X, AlertTriangle, CalendarDays, ShoppingCart, Info, ChevronDown, ChevronUp, FileText, Scale, Receipt, RotateCcw, Fingerprint, Smartphone, Heart } from 'lucide-react'
import { useLegalContent } from '../hooks/useLegalContent'
import TabBar from '../components/TabBar'
import AnnouncementOptIn from '../components/AnnouncementOptIn'
import PushSetupCard from '../components/PushSetupCard'
import LanguageSwitcher from '../components/LanguageSwitcher'
import { useStore } from '../store/useStore'
import type { CreditCard, User } from '../store/useStore'
import { useMyReservations } from '../hooks/useMyReservations'
import { useMyOrders } from '../hooks/useMyOrders'
import { isSelfOrderEnabled } from '../lib/features'
import { useI18n } from '../i18n/useI18n'
import { getCurrency, formatHomeCurrency } from '../utils/currency'
import { showToast } from '../components/Toast'
import { isWeb3Mode } from '../lib/authMode'
import DietaryModal from './StoreDetail/DietaryModal'
import { api } from '../services/api'
import { auth } from '../lib/firebase'
import { hasTotpFactor } from '../lib/totpMfa'
import { getGuestName, setGuestName } from '../lib/guestProfile'
import ResidenceSelect from '../components/ResidenceSelect'
import { asksResidence, isCompleteResidence, type Residence } from '../lib/residence'

export default function Profile() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  // #392 ご利用上限セクションの開閉状態
  const [limitsExpanded, setLimitsExpanded] = useState(false)
  const { user, userId, isLoggedIn, wallet, transactions, logout, deleteAccount, cards, addCard, removeCard, setDefaultCard, refreshChainBalance, favoriteStores } = useStore()
  // #1246: ゲストの表示名 (localStorage のみ、アカウントに影響しない)
  const [guestNameDraft, setGuestNameDraft] = useState(getGuestName())
  const [savedGuestName, setSavedGuestName] = useState(getGuestName())
  const { active: activeReservations } = useMyReservations()
  const activeReservationsCount = activeReservations.length
  const { orders: allMyOrders } = useMyOrders()
  // Mirrors Home.tsx: only orders that still need customer attention.
  // Delivered delivery orders don't count (even within the 30min grace)
  // — they're surfaced from /history instead.
  const activeOrdersCount = allMyOrders.filter((o) => {
    if (o.status === 'cancelled') return false
    if (o.orderType === 'delivery') {
      const inPipeline =
        o.status === 'received' || o.status === 'preparing' || o.status === 'out_for_delivery'
      return inPipeline || !!o.missingReported
    }
    return !o.paid
  }).length
  const { t, locale } = useI18n()
  const currency = getCurrency(locale)

  const balanceJPYC = wallet?.balanceJPYC ?? 0
  const shortAddress = wallet?.address
    ? `${wallet.address.slice(0, 6)}...${wallet.address.slice(-4)}`
    : '---'

  const chargeTotal = transactions
    .filter((tx) => tx.type === 'charge' && tx.status === 'completed')
    .reduce((sum, tx) => sum + tx.amount, 0)

  const handleCopyAddress = () => {
    if (wallet?.address) {
      navigator.clipboard?.writeText(wallet.address)
      showToast({ type: 'success', message: 'Copied!' })
    }
  }

  const handleLogout = () => {
    logout()
    navigate('/', { replace: true })
  }

  // Delete account modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleting, setDeleting] = useState(false)

  // Dietary preferences modal state
  const [showDietaryModal, setShowDietaryModal] = useState(false)

  // Legal docs modal state (#271)
  const [legalModalType, setLegalModalType] = useState<
    'terms' | 'privacy' | 'tokushoho' | 'refundPolicy' | null
  >(null)
  // 本文は開く時にだけ読み込む (メインの JS に入れない — hooks/useLegalContent.ts)
  const legalContent = useLegalContent(locale, !!legalModalType)
  const legalSectionItems: {
    key: 'terms' | 'privacy' | 'tokushoho' | 'refundPolicy'
    icon: typeof FileText
    label: string
  }[] = [
    { key: 'terms', icon: FileText, label: t('profile.terms') },
    { key: 'privacy', icon: Scale, label: t('profile.privacy') },
    { key: 'tokushoho', icon: Receipt, label: t('profile.tokushoho') },
    { key: 'refundPolicy', icon: RotateCcw, label: t('profile.refundPolicy') },
  ]
  const legalModalTitle = legalModalType ? t(`profile.${legalModalType}`) : ''
  const legalModalBody = legalModalType
    ? (legalContent?.[legalModalType] ?? t('common.loading')) : ''

  // Auto-open dietary modal when ?openDietary=true
  useEffect(() => {
    if (searchParams.get('openDietary') === 'true') {
      setShowDietaryModal(true)
      searchParams.delete('openDietary')
      setSearchParams(searchParams, { replace: true })
    }
  }, [searchParams, setSearchParams])

  const handleDeleteAccount = async () => {
    setDeleting(true)
    try {
      await deleteAccount()
      navigate('/', { replace: true })
    } finally {
      setDeleting(false)
      setShowDeleteModal(false)
    }
  }

  // Chain balance query state
  const [chainQuerying, setChainQuerying] = useState(false)

  // Card management state
  const [showCardForm, setShowCardForm] = useState(false)
  const [cardNumber, setCardNumber] = useState('')

  // Self-order display preference state (#314 task #4)
  const [nicknameDraft, setNicknameDraft] = useState(user?.nickname ?? '')
  const [displayPrefDraft, setDisplayPrefDraft] = useState<'tag' | 'name' | 'nickname'>(
    user?.displayPreference ?? 'tag',
  )
  const [savingDisplay, setSavingDisplay] = useState(false)
  useEffect(() => {
    setNicknameDraft(user?.nickname ?? '')
    setDisplayPrefDraft(user?.displayPreference ?? 'tag')
  }, [user?.nickname, user?.displayPreference])

  const displayDirty =
    nicknameDraft.trim() !== (user?.nickname ?? '') ||
    displayPrefDraft !== (user?.displayPreference ?? 'tag')

  // #1207 紙レシート設定の変更 (ラジオ選択で即保存)
  const [savingPaperReceipt, setSavingPaperReceipt] = useState(false)
  const handleSavePaperReceipt = async (wants: boolean) => {
    if (!userId || savingPaperReceipt || (user?.wantsPaperReceipt ?? false) === wants) return
    setSavingPaperReceipt(true)
    try {
      // #2005 **名前と国籍は送らない。** 紙レシートの設定を変えただけで
      // 国籍が 'US' に書き換わりうる作りだった
      await api.updateProfile(userId, { wantsPaperReceipt: wants })
      useStore.setState((state) => ({
        user: state.user ? { ...state.user, wantsPaperReceipt: wants } : state.user,
      }))
      showToast({ type: 'success', message: t('profile.dietarySaved') })
    } catch {
      showToast({ type: 'error', message: t('cart.orderFailed') })
    } finally {
      setSavingPaperReceipt(false)
    }
  }

  // #1274 セグメント推薦用の任意属性 (年代/性別)。選択で即保存
  const [savingSegment, setSavingSegment] = useState(false)
  // #2004 居住地の入力中の値。県だけ選んだ途中の状態を持つために、保存済みの値とは別に持つ
  const [residenceDraft, setResidenceDraft] = useState<Residence>({
    prefecture: user?.residencePrefecture ?? '', city: user?.residenceCity ?? '',
  })
  // セッションの復元が後から届いたとき・保存が終わったときに、保存済みの値へ合わせる
  useEffect(() => {
    setResidenceDraft({
      prefecture: user?.residencePrefecture ?? '', city: user?.residenceCity ?? '',
    })
  }, [user?.residencePrefecture, user?.residenceCity])
  const handleSaveSegment = async (patch: {
    ageBand?: User['ageBand']
    gender?: User['gender']
    // #2004 居住地は必ず 2 つ一組で保存する
    residencePrefecture?: string
    residenceCity?: string
    residenceMailOptOut?: boolean
  }): Promise<boolean> => {
    if (!userId || savingSegment) return false
    const isResidence = patch.residencePrefecture !== undefined
    const isMail = patch.residenceMailOptOut !== undefined
    setSavingSegment(true)
    try {
      await api.updateProfile(userId, patch)
      useStore.setState((state) => ({
        user: state.user ? { ...state.user, ...patch } : state.user,
      }))
      showToast({
        type: 'success',
        message: t(isMail ? 'residence.mailSaved' : isResidence ? 'residence.saved' : 'profile.dietarySaved'),
      })
      return true
    } catch {
      showToast({
        type: 'error',
        message: t(isMail ? 'residence.mailSaveFailed' : isResidence ? 'residence.saveFailed' : 'cart.orderFailed'),
      })
      return false
    } finally {
      setSavingSegment(false)
    }
  }

  // #2004 居住地の入力。**画面に出ている値と、保存されている値を食い違わせない。**
  //   - 県と市が揃った → 保存する
  //   - どちらかを「選択しない」に戻した → **両方を未回答に戻して保存する**。市だけ空にして
  //     保存しないと、画面は消えたように見えるのにサーバには前の市が残る (任意で預かっている
  //     情報の取り下げが、黙って効かないことになる)
  //   - 県だけ変えた (市はこれから選ぶ) → まだ保存しない。「未保存」と画面に出す
  //   - 保存に失敗した → 入力を保存済みの値に戻す
  const savedResidence: Residence = {
    prefecture: user?.residencePrefecture ?? '', city: user?.residenceCity ?? '',
  }
  const handleResidenceChange = async (next: Residence) => {
    const changedPrefecture = next.prefecture !== residenceDraft.prefecture
    const clearing = !next.prefecture || (!next.city && !changedPrefecture)
    const target: Residence = clearing ? { prefecture: '', city: '' } : next
    setResidenceDraft(target)
    if (!clearing && !isCompleteResidence(target)) return      // 県だけ選んだ途中
    if (target.prefecture === savedResidence.prefecture && target.city === savedResidence.city) return
    const ok = await handleSaveSegment({
      residencePrefecture: target.prefecture, residenceCity: target.city,
    })
    if (!ok) setResidenceDraft(savedResidence)
  }
  const residencePending = !!residenceDraft.prefecture && !residenceDraft.city

  const handleSaveDisplay = async () => {
    if (!userId || savingDisplay) return
    const trimmed = nicknameDraft.trim()
    const finalPref: 'tag' | 'name' | 'nickname' =
      displayPrefDraft === 'nickname' && !trimmed ? 'tag' : displayPrefDraft
    setSavingDisplay(true)
    try {
      await api.updateProfile(userId, {
        nickname: trimmed || undefined,
        displayPreference: finalPref,
      })
      useStore.setState((state) => ({
        user: state.user
          ? { ...state.user, nickname: trimmed || undefined, displayPreference: finalPref }
          : state.user,
      }))
      showToast({ type: 'success', message: t('profile.dietarySaved') })
    } catch {
      showToast({ type: 'error', message: t('cart.orderFailed') })
    } finally {
      setSavingDisplay(false)
    }
  }

  const detectBrand = (number: string): CreditCard['brand'] => {
    const first = number.charAt(0)
    if (first === '4') return 'visa'
    if (first === '5') return 'mastercard'
    if (first === '3') return 'amex'
    return 'visa'
  }

  const brandColors: Record<CreditCard['brand'], string> = {
    visa: '#1A1F71',
    mastercard: '#EB001B',
    amex: '#006FCF',
  }

  const brandLabels: Record<CreditCard['brand'], string> = {
    visa: 'VISA',
    mastercard: 'Mastercard',
    amex: 'AMEX',
  }

  const formatCardNumber = (value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 16)
    return digits.replace(/(.{4})/g, '$1 ').trim()
  }



  const handleAddCard = () => {
    const digits = cardNumber.replace(/\D/g, '')
    if (digits.length < 13) return

    const brand = detectBrand(digits)
    const last4 = digits.slice(-4)

    addCard({
      brand,
      last4,
      expiry: '',
      name: '',
      isDefault: false,
    })

    setCardNumber('')
    setShowCardForm(false)
    showToast({ type: 'success', message: t('card.added') })
  }

  const handleRemoveCard = (id: string) => {
    if (window.confirm(t('card.removeConfirm'))) {
      removeCard(id)
      showToast({ type: 'info', message: t('card.removed') })
    }
  }

  // #973: optional mode ではパスキー未登録ユーザーに、Profile の導線で「推奨」を
  // 控えめに提示（不正利用対策）。登録済 / 取得失敗時はバッジを出さない（fail-safe）。
  const [hasPasskey, setHasPasskey] = useState<boolean | null>(null)
  useEffect(() => {
    if (!isLoggedIn) return // #1246: ゲストは認証必須 API を叩かない
    let cancelled = false
    api.listWebauthnCredentials()
      .then((r) => { if (!cancelled) setHasPasskey((r?.credentials || []).length > 0) })
      .catch(() => { if (!cancelled) setHasPasskey(null) })
    return () => { cancelled = true }
  }, [isLoggedIn])

  // #1104: 設定済みの強要素をパスキー認証行にバッジ表示（一目で分かる導線）。
  // TOTP は flag ON のときだけ判定（PasskeySettings.refreshTotp と同方式）。
  // 取得失敗 / flag OFF は null = バッジ非表示（fail-safe）。
  const [totpEnrolled, setTotpEnrolled] = useState<boolean | null>(null)
  useEffect(() => {
    if (!isLoggedIn) return
    let cancelled = false
    api.getWebauthnConfig()
      .then(async (c) => {
        if (c?.totpFallbackEnabled !== true) return
        await auth.currentUser?.reload()
        if (!cancelled) setTotpEnrolled(hasTotpFactor(auth.currentUser))
      })
      .catch(() => { if (!cancelled) setTotpEnrolled(null) })
    return () => { cancelled = true }
  }, [isLoggedIn])

  // #1107: SMS 復旧の登録状態（flag ON かつ登録済のときだけ ✓ SMS チップ）。
  const [smsRegistered, setSmsRegistered] = useState<boolean | null>(null)
  useEffect(() => {
    if (!isLoggedIn) return
    let cancelled = false
    api.getRecoveryPhoneStatus()
      .then((s) => { if (!cancelled) setSmsRegistered(s.enabled === true && s.registered === true) })
      .catch(() => { if (!cancelled) setSmsRegistered(null) })
    return () => { cancelled = true }
  }, [isLoggedIn])

  const menuItems: { icon: typeof CalendarDays; label: string; action: () => void; badge?: number; recommend?: boolean; recommendHint?: string; statusChips?: string[] }[] = [
    { icon: Heart, label: t('profile.favorites'), action: () => navigate('/favorites'), badge: favoriteStores.length || undefined },
    { icon: CalendarDays, label: t('profile.myReservations'), action: () => navigate('/my-reservations'), badge: activeReservationsCount },
    ...(isSelfOrderEnabled() ? [{
      icon: ShoppingCart, label: t('profile.myOrders'), action: () => navigate('/my-orders'), badge: activeOrdersCount,
    }] : []),
    // #1283: 体験チケットは「予約履歴」に統合表示するため、専用項目は廃止
    { icon: Bell, label: t('profile.notifications'), action: () => navigate('/travel-budget') },
    { icon: Shield, label: t('profile.security'), action: () => navigate('/security') },
    {
      icon: Fingerprint, label: t('profile.passkeys'), action: () => navigate('/profile/passkeys'),
      // #1104: 強要素が1つも無いときだけ「推奨」(TOTP 済ユーザーに推奨を出さない)。
      recommend: hasPasskey === false && totpEnrolled !== true,
      recommendHint: t('profile.passkeysRecommendHint'),
      // #1107: 設定済みの要素を ✓ チップで可視化するのみ（未取得は非表示）。
      // TOTP の「未設定」プッシュ（旧 mutedChips/hint）は撤回:
      // パスキー保持者は device 復旧を passkey self-heal で満たしており、
      // TOTP-MFA は毎ログイン摩擦を足すだけ = 逆効果だったため
      // (memory project_device_approval_gate_status)。TOTP/SMS は opt-in。
      statusChips: [
        ...(hasPasskey === true ? [t('profile.factorPasskey')] : []),
        ...(totpEnrolled === true ? [t('profile.factorTotp')] : []),
        ...(smsRegistered === true ? [t('profile.factorSms')] : []),
      ],
    },
    { icon: Smartphone, label: t('deviceManage.title'), action: () => navigate('/profile/devices') },
    // #1930 「Coming soon!」で行き止まりだった導線を、実際の問い合わせ窓口へ繋ぐ
    { icon: HelpCircle, label: t('profile.help'), action: () => navigate('/contact') },
  ]

  // #1246: ゲスト版プロフィール。表示名 (localStorage のみ・アカウント無関係) と
  // 言語設定はゲストでも使える。他 (予約/注文/セキュリティ/カード等) はログイン後。
  if (!isLoggedIn) {
    const saveGuestName = () => {
      setGuestName(guestNameDraft)
      setSavedGuestName(guestNameDraft.trim())
      showToast({ type: 'success', message: t('profile.guestNameSaved') })
    }
    return (
      <div className="relative min-h-screen bg-warm-white safe-top safe-bottom">
        <div className="pb-[83px]">
          <div className="px-5 pt-6 pb-2">
            <h1 className="font-display text-xl font-bold text-text-primary">
              {t('profile.title')}
            </h1>
          </div>

          {/* Guest card: アバター + 表示名編集 */}
          <div className="mx-4 mt-3 rounded-2xl bg-white p-5 shadow-card">
            <div className="flex items-center gap-4">
              <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-navy to-navy-light text-2xl font-bold text-white">
                {(savedGuestName || t('guest.label')).charAt(0)}
              </div>
              <p className="min-w-0 flex-1 font-display text-lg font-bold text-text-primary">
                {savedGuestName || t('guest.label')}
              </p>
            </div>
            <label className="mt-4 block">
              <span className="font-body text-xs text-text-secondary">
                {t('profile.guestNameLabel')}
              </span>
              <div className="mt-1.5 flex gap-2">
                <input
                  value={guestNameDraft}
                  onChange={(e) => setGuestNameDraft(e.target.value)}
                  maxLength={30}
                  className="min-w-0 flex-1 rounded-lg bg-gray-50 px-3 py-2 font-body text-sm text-text-primary outline-none focus:ring-2 focus:ring-yokoso-red/30"
                />
                <button
                  onClick={saveGuestName}
                  className="tap-effect shrink-0 rounded-lg bg-navy px-4 py-2 font-body text-sm font-medium text-white"
                >
                  {t('profile.guestNameSave')}
                </button>
              </div>
            </label>
          </div>

          {/* 登録導線 (Home と同じ文言) */}
          <button
            onClick={() => {
              sessionStorage.setItem('postLoginRedirect', '/profile')
              navigate('/login')
            }}
            className="tap-card mx-4 mt-4 flex w-[calc(100%-2rem)] items-center gap-3 rounded-2xl bg-navy p-4 text-left shadow-card"
          >
            <div className="min-w-0 flex-1">
              <p className="font-display text-sm font-semibold text-white">
                {t('home.guestCta.title')}
              </p>
              <p className="mt-0.5 font-body text-xs leading-relaxed text-white/80">
                {t('home.guestCta.subtitle')}
              </p>
            </div>
            <ChevronRight size={18} className="shrink-0 text-white/70" />
          </button>

          {/* 言語設定 (ゲストでも有効 — i18n は localStorage 保存) */}
          <div className="mx-4 mt-4 flex items-center justify-between rounded-2xl bg-white p-4 shadow-card">
            <div className="flex items-center gap-2.5">
              <Globe size={18} className="text-text-tertiary" />
              <span className="font-body text-sm text-text-primary">{t('profile.language')}</span>
            </div>
            <LanguageSwitcher className="!gap-0.5" />
          </div>

          {/* #1930 問い合わせ。**ゲスト側にこそ要る** — 「ログインできない」人は
            * ログイン後の画面に辿り着けないので、そこに置いても届かない。 */}
          <button
            onClick={() => navigate('/contact')}
            className="tap-card mx-4 mt-4 flex w-[calc(100%-2rem)] items-center justify-between rounded-2xl bg-white p-4 shadow-card"
          >
            <div className="flex items-center gap-2.5">
              <HelpCircle size={18} className="text-text-tertiary" />
              <span className="font-body text-sm text-text-primary">{t('contact.title')}</span>
            </div>
            <ChevronRight size={18} className="shrink-0 text-text-tertiary" />
          </button>
        </div>
        <TabBar />
      </div>
    )
  }

  return (
    <div className="relative min-h-screen bg-warm-white safe-top safe-bottom">
      <div className="pb-[83px]">
        {/* Header */}
        <div className="px-5 pt-6 pb-2">
          <h1 className="font-display text-xl font-bold text-text-primary">
            {t('profile.title')}
          </h1>
        </div>

        {/* User Card */}
        <div className="mx-4 mt-3 rounded-2xl bg-white p-5 shadow-card">
          <div className="flex items-center gap-4">
            {/* Avatar */}
            <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-navy to-navy-light text-2xl font-bold text-white">
              {user?.name?.charAt(0) ?? 'A'}
            </div>
            {/* Info */}
            <div className="min-w-0 flex-1">
              <p className="font-display text-lg font-bold text-text-primary">
                {user?.name ?? 'Alex Johnson'}
              </p>
              <p className="mt-0.5 truncate font-body text-sm text-text-secondary">
                {user?.email ?? 'alex@example.com'}
              </p>
            </div>
          </div>

          {/* Wallet Address (Web3 mode only) */}
          {isWeb3Mode() && (
            <div className="mt-4 flex items-center justify-between rounded-lg bg-gray-50 px-3 py-2.5">
              <div className="flex items-center gap-2">
                <Wallet size={16} className="text-text-tertiary" />
                <span className="font-mono text-xs text-text-secondary">{shortAddress}</span>
              </div>
              <button onClick={handleCopyAddress} className="tap-effect p-1">
                <Copy size={14} className="text-text-tertiary" />
              </button>
            </div>
          )}
        </div>

        {/* Stats Grid */}
        <div className={`mx-4 mt-4 grid gap-3 ${isWeb3Mode() ? 'grid-cols-2' : 'grid-cols-1'}`}>
          <div className="rounded-xl bg-white p-4 shadow-card">
            <p className="font-body text-xs text-text-tertiary">{t('profile.transactionCount')}</p>
            <p className="mt-1 font-display text-2xl font-bold text-navy">
              {transactions.length}
            </p>
          </div>
          {isWeb3Mode() && (
            <div className="rounded-xl bg-white p-4 shadow-card">
              <p className="font-body text-xs text-text-tertiary">{t('profile.totalCharged')}</p>
              <p className="mt-1 font-display text-2xl font-bold text-navy">
                &yen;{chargeTotal.toLocaleString()}
              </p>
            </div>
          )}
        </div>

        {/* Balance Section - Dual Balance (Web3 mode only) */}
        {isWeb3Mode() && <div className="mx-4 mt-4 rounded-xl bg-gradient-to-r from-navy to-navy-light p-4 shadow-card">
          {/* App Balance (Firebase) */}
          <div className="flex items-center justify-between">
            <p className="font-body text-xs text-white/70">{t('profile.appBalance')}</p>
            <span className="rounded-full bg-matcha/20 px-2 py-0.5 text-[10px] font-bold text-matcha">YOKOSO</span>
          </div>
          <p className="mt-1 font-display text-2xl font-bold text-gold">
            &yen; {balanceJPYC.toLocaleString()}
          </p>
          {formatHomeCurrency(balanceJPYC, locale) && (
            <p className="mt-0.5 font-body text-xs text-white/60">
              {formatHomeCurrency(balanceJPYC, locale)}
            </p>
          )}

          {/* Divider */}
          <div className="my-3 border-t border-white/10" />

          {/* Chain Balance (Polygon JPYC) */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              <Link2 size={12} className="text-white/50" />
              <p className="font-body text-xs text-white/70">{t('profile.chainBalance')}</p>
            </div>
            <button
              onClick={async () => {
                setChainQuerying(true)
                try {
                  await refreshChainBalance()
                  const updated = useStore.getState().wallet
                  if (updated?.chainBalanceJPYC !== null && updated?.chainBalanceJPYC !== undefined) {
                    showToast({ type: 'success', message: `${t('profile.chainBalance')}: ¥${updated.chainBalanceJPYC.toLocaleString()}` })
                  } else {
                    showToast({ type: 'error', message: t('profile.chainQueryFailed') })
                  }
                } catch {
                  showToast({ type: 'error', message: t('profile.chainQueryFailed') })
                }
                setChainQuerying(false)
              }}
              disabled={chainQuerying}
              className="flex items-center gap-1 rounded-full bg-white/10 px-2 py-0.5 text-[10px] font-medium text-white/70 transition-colors hover:bg-white/20 disabled:opacity-50"
            >
              <RefreshCw size={10} className={chainQuerying ? 'animate-spin' : ''} />
              {t('profile.refreshChain')}
            </button>
          </div>
          <div className="mt-1 flex items-end gap-2">
            <p className="font-display text-lg font-bold text-white/90">
              {wallet?.chainBalanceJPYC !== null && wallet?.chainBalanceJPYC !== undefined
                ? `¥ ${wallet.chainBalanceJPYC.toLocaleString()}`
                : chainQuerying
                  ? t('profile.chainQuerying')
                  : t('profile.chainNotConnected')
              }
            </p>
            {wallet?.chainBalanceJPYC !== null && wallet?.chainBalanceJPYC !== undefined && (
              <span className={`mb-0.5 rounded-full px-2 py-0.5 text-[10px] font-bold ${
                wallet.chainBalanceJPYC === balanceJPYC
                  ? 'bg-matcha/20 text-matcha'
                  : 'bg-gold/20 text-gold'
              }`}>
                {wallet.chainBalanceJPYC === balanceJPYC ? t('profile.chainSynced') : t('profile.chainDiff')}
              </span>
            )}
          </div>
        </div>}

        {/* Saved Cards Section */}
        <div className="mx-4 mt-4 rounded-xl bg-white shadow-card">
          {/* Section Header */}
          <div className="flex items-center justify-between px-4 pt-4 pb-2">
            <div className="flex items-center gap-2">
              <CreditCardIcon size={18} className="text-text-tertiary" />
              <span className="font-display text-sm font-bold text-text-primary">
                {t('card.title')}
              </span>
            </div>
            <button
              onClick={() => setShowCardForm(!showCardForm)}
              className="tap-effect flex items-center gap-1 rounded-lg bg-navy/5 px-3 py-1.5 font-body text-xs font-medium text-navy transition-colors hover:bg-navy/10"
            >
              <Plus size={14} />
              {t('card.addNew')}
            </button>
          </div>

          {/* Add Card Form */}
          {showCardForm && (
            <div className="mx-4 mt-2 rounded-lg border border-gray-100 bg-gray-50 p-4">
              <div className="space-y-3">
                {/* Card Number */}
                <div>
                  <label className="font-body text-xs text-text-tertiary">
                    {t('card.number')}
                  </label>
                  <div className="relative mt-1">
                    <input
                      type="text"
                      value={formatCardNumber(cardNumber)}
                      onChange={(e) => setCardNumber(e.target.value.replace(/\D/g, '').slice(0, 16))}
                      placeholder="4242 4242 4242 4242"
                      className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 pr-16 font-mono text-sm text-text-primary placeholder:text-text-disabled focus:border-navy focus:outline-none focus:ring-1 focus:ring-navy"
                    />
                    {cardNumber.length > 0 && (
                      <span
                        className="absolute right-3 top-1/2 -translate-y-1/2 rounded px-1.5 py-0.5 text-[10px] font-bold text-white"
                        style={{ backgroundColor: brandColors[detectBrand(cardNumber)] }}
                      >
                        {brandLabels[detectBrand(cardNumber)]}
                      </span>
                    )}
                  </div>
                </div>

                {/* Form Buttons */}
                <div className="flex gap-2 pt-1">
                  <button
                    onClick={() => {
                      setShowCardForm(false)
                      setCardNumber('')
                    }}
                    className="tap-effect flex-1 rounded-lg border border-gray-200 bg-white py-2.5 font-body text-sm font-medium text-text-secondary transition-colors hover:bg-gray-50"
                  >
                    {t('card.cancel')}
                  </button>
                  <button
                    onClick={handleAddCard}
                    disabled={cardNumber.replace(/\D/g, '').length < 13}
                    className="tap-effect flex-1 rounded-lg bg-navy py-2.5 font-body text-sm font-medium text-white transition-colors hover:bg-navy-light disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {t('card.save')}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Card List */}
          {cards.length === 0 ? (
            <div className="px-4 py-8 text-center">
              <CreditCardIcon size={32} className="mx-auto text-text-disabled" />
              <p className="mt-2 font-display text-sm font-medium text-text-secondary">
                {t('card.noCards')}
              </p>
              <p className="mt-1 font-body text-xs text-text-tertiary">
                {t('card.noCardsDesc')}
              </p>
            </div>
          ) : (
            <div className="px-4 pb-2 pt-2">
              {cards.map((card) => (
                <div
                  key={card.id}
                  className="group mb-2 flex items-center gap-3 rounded-lg border border-gray-100 bg-gray-50 p-3 last:mb-0 transition-colors hover:bg-gray-100"
                >
                  {/* Brand Badge */}
                  <div
                    className="flex h-10 w-14 shrink-0 items-center justify-center rounded-md text-[10px] font-bold text-white"
                    style={{ backgroundColor: brandColors[card.brand] }}
                  >
                    {brandLabels[card.brand]}
                  </div>

                  {/* Card Info */}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-sm font-medium text-text-primary">
                        {'•••• ' + card.last4}
                      </span>
                      {card.isDefault && (
                        <span className="rounded-full bg-matcha/10 px-2 py-0.5 text-[10px] font-bold text-matcha">
                          {t('card.default')}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex shrink-0 items-center gap-1">
                    {!card.isDefault && (
                      <button
                        onClick={() => setDefaultCard(card.id)}
                        className="tap-effect rounded-md px-2 py-1.5 font-body text-[10px] font-medium text-navy transition-colors hover:bg-navy/5"
                      >
                        {t('card.setDefault')}
                      </button>
                    )}
                    <button
                      onClick={() => handleRemoveCard(card.id)}
                      className="tap-effect rounded-md p-1.5 text-text-disabled transition-colors hover:bg-red-50 hover:text-red-500"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Info Section */}
        <div className="mx-4 mt-4 rounded-xl bg-white shadow-card">
          <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3.5">
            <div className="flex items-center gap-3">
              <Globe size={18} className="text-text-tertiary" />
              <span className="font-body text-sm text-text-primary">{t('profile.language')}</span>
            </div>
            <LanguageSwitcher className="!gap-0.5" />
          </div>
          <div className="flex items-center justify-between px-4 py-3.5">
            <div className="flex items-center gap-3">
              <Wallet size={18} className="text-text-tertiary" />
              <span className="font-body text-sm text-text-primary">{t('profile.currency')}</span>
            </div>
            <span className="font-body text-sm text-text-secondary">
              {currency.code} ({currency.symbol})
            </span>
          </div>
        </div>

        {/* Self-order display preference (#314 task #4) */}
        {isSelfOrderEnabled() && (
          <div className="mx-4 mt-4 rounded-xl bg-white p-4 shadow-card">
            <p className="font-display text-sm font-bold text-text-primary">
              {t('profile.displayPrefSection')}
            </p>
            <p className="mt-1 font-body text-xs text-text-tertiary leading-relaxed">
              {t('profile.displayPrefNote')}
            </p>

            <div className="mt-3">
              <label className="font-body text-xs text-text-tertiary">
                {t('profile.nicknameLabel')}
              </label>
              <input
                type="text"
                value={nicknameDraft}
                onChange={(e) => setNicknameDraft(e.target.value)}
                placeholder={t('profile.nicknamePlaceholder')}
                maxLength={24}
                className="mt-1 w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 font-body text-sm text-text-primary outline-none focus:border-navy focus:ring-1 focus:ring-navy"
              />
            </div>

            <div className="mt-3 space-y-2">
              {(['tag', 'name', 'nickname'] as const).map((opt) => {
                const disabled = opt === 'nickname' && !nicknameDraft.trim()
                const label =
                  opt === 'tag'
                    ? t('profile.displayPref.tag')
                    : opt === 'name'
                      ? t('profile.displayPref.name', { name: user?.name || '...' })
                      : t('profile.displayPref.nickname', { nickname: nicknameDraft.trim() || '...' })
                return (
                  <label
                    key={opt}
                    className={`flex items-center gap-3 rounded-lg border px-3 py-2 cursor-pointer transition-colors ${
                      displayPrefDraft === opt
                        ? 'border-navy bg-navy/5'
                        : 'border-gray-200 bg-white'
                    } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <input
                      type="radio"
                      name="profileDisplayPref"
                      value={opt}
                      checked={displayPrefDraft === opt}
                      disabled={disabled}
                      onChange={() => !disabled && setDisplayPrefDraft(opt)}
                      className="h-4 w-4 text-navy accent-navy"
                    />
                    <span className="font-body text-[13px] text-text-primary">{label}</span>
                  </label>
                )
              })}
            </div>

            <button
              onClick={handleSaveDisplay}
              disabled={!displayDirty || savingDisplay}
              className={`mt-3 w-full rounded-lg py-2.5 font-display text-sm font-semibold transition-colors ${
                displayDirty && !savingDisplay
                  ? 'bg-navy text-white hover:bg-navy-light'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
            >
              {savingDisplay ? '...' : t('profile.save')}
            </button>
          </div>
        )}

        {/* #1207 紙レシート設定 — 初回登録 (ProfileSetup) で選んだ内容をあとから変更する場所。
            効くのはアプリ決済時の店頭自動印刷のみ */}
        <div className="mx-4 mt-4 rounded-xl bg-white p-4 shadow-card">
          <p className="font-display text-sm font-bold text-text-primary">
            🧾 {t('receiptPref.title')}
          </p>
          <p className="mt-1 font-body text-xs text-text-tertiary leading-relaxed">
            {t('receiptPref.hint')}
          </p>
          <div className="mt-3 space-y-2">
            {([false, true] as const).map((wants) => (
              <label
                key={String(wants)}
                className={`flex items-center gap-3 rounded-lg border px-3 py-2 cursor-pointer transition-colors ${
                  (user?.wantsPaperReceipt ?? false) === wants
                    ? 'border-navy bg-navy/5'
                    : 'border-gray-200 bg-white'
                }`}
              >
                <input
                  type="radio"
                  name="profilePaperReceipt"
                  checked={(user?.wantsPaperReceipt ?? false) === wants}
                  disabled={savingPaperReceipt}
                  onChange={() => void handleSavePaperReceipt(wants)}
                  className="h-4 w-4 text-navy accent-navy"
                />
                <span className="font-body text-[13px] text-text-primary">
                  {wants ? t('receiptPref.yes') : t('receiptPref.no')}
                </span>
              </label>
            ))}
          </div>
        </div>

        {/* #1274 セグメント推薦用の任意属性 — 同じ年代/国の旅行者の実績から
            おすすめを出すために使う。未回答のままでも全機能が使える */}
        <div className="mx-4 mt-4 rounded-xl bg-white p-4 shadow-card">
          <p className="font-display text-sm font-bold text-text-primary">
            🧭 {t('profile.segTitle')}
          </p>
          <p className="mt-1 font-body text-xs text-text-tertiary leading-relaxed">
            {t('profile.segHint')}
          </p>
          <div className="mt-3 flex gap-2">
            <label className="min-w-0 flex-1">
              <span className="font-body text-xs text-text-tertiary">{t('profile.ageBand')}</span>
              <select
                value={user?.ageBand ?? ''}
                disabled={savingSegment}
                onChange={(e) =>
                  void handleSaveSegment({ ageBand: e.target.value as User['ageBand'] })
                }
                className="mt-1 w-full rounded-lg border border-gray-200 bg-white px-2.5 py-2 font-body text-[13px] text-text-primary"
              >
                <option value="">{t('seg.notSet')}</option>
                {(['10s', '20s', '30s', '40s', '50s', '60s+'] as const).map((v) => (
                  <option key={v} value={v}>{t(`seg.age.${v}`)}</option>
                ))}
              </select>
            </label>
            <label className="min-w-0 flex-1">
              <span className="font-body text-xs text-text-tertiary">{t('profile.gender')}</span>
              <select
                value={user?.gender ?? ''}
                disabled={savingSegment}
                onChange={(e) =>
                  void handleSaveSegment({ gender: e.target.value as User['gender'] })
                }
                className="mt-1 w-full rounded-lg border border-gray-200 bg-white px-2.5 py-2 font-body text-[13px] text-text-primary"
              >
                <option value="">{t('seg.notSet')}</option>
                {(['female', 'male', 'other'] as const).map((v) => (
                  <option key={v} value={v}>{t(`seg.gender.${v}`)}</option>
                ))}
              </select>
            </label>
          </div>
        </div>

        {/* #2004 居住地 (任意)。国籍が日本の人にだけ出す。県と市が揃った時と、未選択に戻した時に
            保存する — 県だけ選んだ途中の状態はサーバに送らない (サーバは片方だけを断る) */}
        {asksResidence(user?.nationality) && (
          <div className="mx-4 mt-4 rounded-xl bg-white p-4 shadow-card">
            <p className="font-display text-sm font-bold text-text-primary">
              🏠 {t('residence.title')}
            </p>
            <p className="mt-1 font-body text-xs text-text-tertiary leading-relaxed">
              {t('residence.help')}
            </p>
            <div className="mt-2">
              <ResidenceSelect
                value={residenceDraft}
                disabled={savingSegment}
                onChange={(next) => void handleResidenceChange(next)}
                selectClassName="mt-1 w-full rounded-lg border border-gray-200 bg-white px-2.5 py-2 font-body text-[13px] text-text-primary disabled:bg-gray-50 disabled:text-text-tertiary"
              />
              {residencePending && (
                <p className="mt-1.5 font-body text-xs text-amber-700">{t('residence.pendingCity')}</p>
              )}
            </div>
            {/* #2004 地域のお知らせメール。同意は居住地の登録で取っている (上の文言) ので、
              * ここは「止める」ためのスイッチ。居住地が入っている人にだけ出す */}
            {isCompleteResidence(savedResidence) && (
              <label className="mt-3 flex items-center justify-between gap-3 border-t border-gray-100 pt-3">
                <span className="min-w-0">
                  <span className="block font-body text-sm text-text-primary">{t('residence.mailToggle')}</span>
                  <span className="mt-0.5 block font-body text-[11px] leading-snug text-text-tertiary">
                    {t('residence.mailToggleHint')}
                  </span>
                </span>
                <input
                  type="checkbox"
                  role="switch"
                  aria-checked={!user?.residenceMailOptOut}
                  checked={!user?.residenceMailOptOut}
                  disabled={savingSegment}
                  onChange={(e) => void handleSaveSegment({ residenceMailOptOut: !e.target.checked })}
                  className="h-5 w-9 shrink-0 accent-matcha"
                />
              </label>
            )}
          </div>
        )}

        {/* Dietary Preferences */}
        <div className="mx-4 mt-4 rounded-xl bg-white shadow-card">
          <button
            onClick={() => setShowDietaryModal(true)}
            className="tap-effect flex w-full items-center justify-between px-4 py-3.5"
          >
            <div className="flex items-center gap-3">
              <span className="text-lg">🍽️</span>
              <div className="text-left">
                <p className="font-body text-sm text-text-primary">{t('profile.dietarySettings')}</p>
                <p className="font-body text-xs text-text-tertiary mt-0.5">
                  {(user?.dietaryAllergens?.length ?? 0) + (user?.dietaryStyles?.length ?? 0) > 0
                    ? t('profile.dietarySettingsCount', { count: (user?.dietaryAllergens?.length ?? 0) + (user?.dietaryStyles?.length ?? 0) })
                    : t('profile.dietarySettingsNone')}
                </p>
              </div>
            </div>
            <ChevronRight size={16} className="text-text-disabled" />
          </button>
        </div>

        {/* #1384 Phase 2 旅先のお知らせ通知の受信設定。**業務通知（注文の呼び出し）
          * とは別の同意**にしている — 混ぜると案内をうるさく感じた人が通知ごと切り、
          * 本当に必要な呼び出しまで届かなくなるため。 */}
        {/* #1990 通知を受け取る準備への常設の入口。注文履歴のシートは一度閉じると
          * 二度と出ないので、ここから戻れるようにする */}
        <div className="mx-4 mt-4">
          <PushSetupCard context="profile" />
        </div>
        <div className="mx-4 mt-4">
          <AnnouncementOptIn />
        </div>

        {/* Menu Items */}
        <div className="mx-4 mt-4 rounded-xl bg-white shadow-card">
          {menuItems.map((item, i) => (
            <button
              key={item.label}
              onClick={item.action}
              className={`tap-effect flex w-full items-center justify-between px-4 py-3.5 ${
                i < menuItems.length - 1 ? 'border-b border-gray-100' : ''
              }`}
            >
              <div className="flex items-center gap-3">
                <item.icon size={18} className="text-text-tertiary" />
                <div className="flex flex-col items-start">
                  <div className="flex items-center gap-1.5">
                    <span className="font-body text-sm text-text-primary">{item.label}</span>
                    {item.recommend && (
                      <span className="rounded-full bg-matcha/15 text-matcha px-1.5 py-0.5 text-[10px] font-body font-bold">
                        {t('profile.recommended')}
                      </span>
                    )}
                    {/* #1104: 設定済み要素チップ (パスキー / TOTP / SMS)。
                        #1107: TOTP「未設定」プッシュは撤回(opt-in 化)。 */}
                    {item.statusChips?.map((chip) => (
                      <span
                        key={chip}
                        className="rounded-full bg-matcha/15 text-matcha px-1.5 py-0.5 text-[10px] font-body font-bold"
                      >
                        ✓ {chip}
                      </span>
                    ))}
                  </div>
                  {item.recommend && item.recommendHint && (
                    <span className="font-body text-[10px] text-text-tertiary mt-0.5 text-left leading-tight">
                      {item.recommendHint}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {item.badge !== undefined && item.badge > 0 && (
                  <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-yokoso-red px-1.5 text-[10px] font-body font-bold text-white">
                    {item.badge}
                  </span>
                )}
                <ChevronRight size={16} className="text-text-disabled" />
              </div>
            </button>
          ))}
        </div>

        {/* #392 ご利用上限について (collapsible) */}
        <div className="mx-4 mt-4 rounded-xl bg-white shadow-card">
          <button
            onClick={() => setLimitsExpanded((prev) => !prev)}
            className="tap-effect flex w-full items-center justify-between px-4 py-3.5"
            aria-expanded={limitsExpanded}
          >
            <div className="flex items-center gap-3">
              <Info size={18} className="text-text-tertiary" />
              <span className="font-body text-sm text-text-primary">{t('profile.limitsTitle')}</span>
            </div>
            {limitsExpanded ? (
              <ChevronUp size={16} className="text-text-disabled" />
            ) : (
              <ChevronDown size={16} className="text-text-disabled" />
            )}
          </button>
          {limitsExpanded && (
            <div className="border-t border-gray-100 px-4 py-3 font-body text-xs text-text-secondary space-y-2">
              <p>{t('profile.limitsDescription')}</p>
              <ul className="list-disc pl-4 space-y-1">
                <li>{t('profile.limitPerTxLabel')}</li>
                <li>{t('profile.limitUserDailyLabel')}</li>
                <li>{t('profile.limitCardDailyLabel')}</li>
                <li>{t('profile.limitRateLabel')}</li>
              </ul>
              <div className="mt-2">
                <p className="font-semibold text-text-primary">{t('profile.limitsWhyTitle')}</p>
                <p>{t('profile.limitsWhyBody')}</p>
              </div>
              <div className="mt-2">
                <p className="font-semibold text-text-primary">{t('profile.limitsResetTitle')}</p>
                <p>{t('profile.limitsResetBody')}</p>
              </div>
            </div>
          )}
        </div>

        {/* #271 Legal Section: 利用規約 / プライバシー / 特商法 / 返金ポリシー */}
        <div className="mx-4 mt-4">
          <p className="mb-2 px-1 font-body text-[11px] font-semibold uppercase tracking-wider text-text-tertiary">
            {t('profile.legalSection')}
          </p>
          <div className="rounded-xl bg-white shadow-card">
            {legalSectionItems.map((item, i) => (
              <button
                key={item.key}
                onClick={() => setLegalModalType(item.key)}
                className={`tap-effect flex w-full items-center justify-between px-4 py-3.5 ${
                  i < legalSectionItems.length - 1 ? 'border-b border-gray-100' : ''
                }`}
              >
                <div className="flex items-center gap-3">
                  <item.icon size={18} className="text-text-tertiary" />
                  <span className="font-body text-sm text-text-primary">{item.label}</span>
                </div>
                <ChevronRight size={16} className="text-text-disabled" />
              </button>
            ))}
          </div>
        </div>

        {/* Logout */}
        <div className="mx-4 mt-4">
          <button
            onClick={handleLogout}
            className="tap-effect flex w-full items-center justify-center gap-2 rounded-xl border border-red-200 bg-white py-3.5 font-body text-sm font-medium text-red-500 shadow-card transition-colors hover:bg-red-50"
          >
            <LogOut size={18} />
            {t('profile.logout')}
          </button>
        </div>

        {/* Delete Account */}
        <div className="mx-4 mt-3">
          <button
            onClick={() => setShowDeleteModal(true)}
            className="tap-effect flex w-full items-center justify-center gap-2 py-3.5 font-body text-xs text-text-tertiary transition-colors hover:text-red-500"
          >
            <UserX size={14} />
            {t('profile.deleteAccount')}
          </button>
        </div>

        {/* Version */}
        <p className="mt-2 pb-4 text-center font-mono text-[10px] text-text-disabled">
          {t('profile.version')} 1.0.0 (Demo)
        </p>

        <DietaryModal open={showDietaryModal} onClose={() => setShowDietaryModal(false)} />

        {/* #271 Legal Document Modal */}
        {legalModalType && (
          <div
            className="fixed inset-0 z-50 flex items-end justify-center bg-black/40"
            onClick={() => setLegalModalType(null)}
          >
            <div
              className="animate-slide-up w-full max-w-[430px] rounded-t-2xl bg-white shadow-xl"
              style={{ maxHeight: '85vh' }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
                <h3 className="font-display text-[16px] font-bold text-text-primary">
                  {legalModalTitle}
                </h3>
                <button
                  onClick={() => setLegalModalType(null)}
                  className="tap-effect flex h-8 w-8 items-center justify-center rounded-full hover:bg-gray-100 transition-colors"
                >
                  <X size={18} className="text-text-secondary" />
                </button>
              </div>
              <div className="overflow-y-auto px-5 py-4" style={{ maxHeight: 'calc(85vh - 64px)' }}>
                <pre className="whitespace-pre-wrap font-body text-[13px] leading-relaxed text-text-secondary">
                  {legalModalBody}
                </pre>
              </div>
            </div>
          </div>
        )}

        {showDeleteModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-6">
            <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl animate-scale-in">
              <div className="flex items-start justify-between">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-100">
                  <AlertTriangle size={20} className="text-red-500" />
                </div>
                <button onClick={() => setShowDeleteModal(false)} className="tap-effect p-1">
                  <X size={20} className="text-text-tertiary" />
                </button>
              </div>
              <h3 className="mt-4 font-display text-lg font-bold text-text-primary">
                {t('profile.deleteConfirmTitle')}
              </h3>
              <p className="mt-2 font-body text-sm text-text-secondary leading-relaxed">
                {t('profile.deleteConfirmDesc')}
              </p>
              <ul className="mt-3 space-y-1.5">
                <li className="flex items-center gap-2 font-body text-xs text-text-tertiary">
                  <span className="h-1 w-1 rounded-full bg-red-400" />
                  {t('profile.deleteItem1')}
                </li>
                <li className="flex items-center gap-2 font-body text-xs text-text-tertiary">
                  <span className="h-1 w-1 rounded-full bg-red-400" />
                  {t('profile.deleteItem2')}
                </li>
                <li className="flex items-center gap-2 font-body text-xs text-text-tertiary">
                  <span className="h-1 w-1 rounded-full bg-red-400" />
                  {t('profile.deleteItem3')}
                </li>
              </ul>
              <div className="mt-6 flex gap-3">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="tap-effect flex-1 rounded-xl border border-gray-200 bg-white py-3 font-display text-sm font-semibold text-text-primary transition-colors hover:bg-gray-50"
                >
                  {t('profile.deleteCancel')}
                </button>
                <button
                  onClick={handleDeleteAccount}
                  disabled={deleting}
                  className="tap-effect flex-1 rounded-xl bg-red-500 py-3 font-display text-sm font-semibold text-white transition-colors hover:bg-red-600 disabled:opacity-50"
                >
                  {deleting ? '...' : t('profile.deleteConfirm')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <TabBar />
    </div>
  )
}
